import React from 'react';
import {
  Search,
  TrendingUp,
  Sparkles,
  Bookmark,
  FileText,
  Users,
  Globe,
  ArrowUpRight,
  ChevronRight,
  Filter,
  Calendar,
  Unlock,
  Star,
  BarChart3,
  ExternalLink,
  BookOpen,
  Beaker,
  Cpu,
  Zap,
} from 'lucide-react';
import './ExploreMockupContent.css';

const ExploreMockupContent = ({
  animationPhase = 'initial',
  searchText = '',
  typingComplete = false,
  visibleTrendingCards = 0,
  visiblePaperCards = 0,
}) => {
  const trendingTopics = [
    {
      title: 'Machine Learning in Healthcare',
      papers: '12.5K',
      trend: '+8%',
      color: 'blue',
      icon: Beaker,
    },
    {
      title: 'Quantum Computing',
      papers: '8.2K',
      trend: '+15%',
      color: 'indigo',
      icon: Cpu,
    },
    {
      title: 'Climate Science',
      papers: '9.8K',
      trend: '+5%',
      color: 'green',
      icon: Globe,
    },
    {
      title: 'Neural Networks',
      papers: '15.3K',
      trend: '+12%',
      color: 'amber',
      icon: Zap,
    },
    {
      title: 'Computer Vision',
      papers: '11.2K',
      trend: '+9%',
      color: 'cyan',
      icon: Star,
    },
    {
      title: 'Natural Language Processing',
      papers: '13.7K',
      trend: '+18%',
      color: 'pink',
      icon: BookOpen,
    },
    {
      title: 'Robotics & Automation',
      papers: '7.4K',
      trend: '+6%',
      color: 'indigo',
      icon: Cpu,
    },
    {
      title: 'Renewable Energy',
      papers: '10.1K',
      trend: '+11%',
      color: 'green',
      icon: Zap,
    },
  ];

  const paperResults = [
    {
      title: 'Deep Learning for Medical Image Analysis: A Comprehensive Survey',
      authors: ['Hoffmann, L.', 'et al.'],
      year: '2024',
      journal: 'Nature Medicine',
      citations: 1245,
      field: 'Computer Science',
      isOpen: true,
      isTrending: true,
    },
    {
      title: 'Advanced CNN Architectures for Multi-Modal Object Detection',
      authors: ['Richter, T.', 'Fraser, C.'],
      year: '2024',
      journal: 'IEEE CVPR',
      citations: 892,
      field: 'AI Research',
      isOpen: true,
      isRecommended: true,
    },
    {
      title: 'Transformer-Based Image Segmentation in Clinical Applications',
      authors: ['Tanabe, Y.', 'et al.'],
      year: '2024',
      journal: 'ICML',
      citations: 567,
      field: 'Deep Learning',
      isOpen: false,
      isTrending: true,
    },
    {
      title: 'Federated Learning for Privacy-Preserving Healthcare Analytics',
      authors: ['Lindqvist, S.', 'et al.'],
      year: '2024',
      journal: 'Nature Digital Medicine',
      citations: 723,
      field: 'Healthcare AI',
      isOpen: true,
      isRecommended: true,
    },
  ];

  return (
    <div className="explore-mock">
      {/* Header */}
      <header className="explore-mock-header">
        <div className="explore-mock-header-left">
          <div className="explore-mock-title-row">
            <h2 className="explore-mock-title">Explore Papers</h2>
            <div className="explore-mock-badge">
              <Globe size={11} />
              <span>250M+ Papers</span>
            </div>
          </div>
          <p className="explore-mock-subtitle">
            Search millions of academic papers across all fields
          </p>
        </div>
        <button className="explore-mock-saved-btn">
          <Bookmark size={13} />
          <span>Saved</span>
          <span className="explore-mock-saved-count">12</span>
        </button>
      </header>

      {/* Tabs */}
      <div className="explore-mock-tabs">
        <div className="explore-mock-tabs-track">
          <button className="explore-mock-tab explore-mock-tab--active explore-mock-tab--search">
            <Search size={12} />
            <span>Search</span>
          </button>
          <button className="explore-mock-tab explore-mock-tab--trending">
            <TrendingUp size={12} />
            <span>Trending</span>
          </button>
          <button className="explore-mock-tab explore-mock-tab--foryou">
            <Sparkles size={12} />
            <span>For You</span>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="explore-mock-search">
        <div className="explore-mock-search-bar">
          <Search size={14} className="explore-mock-search-icon" />
          <input
            type="text"
            className="explore-mock-search-input"
            placeholder="Search by title, author, DOI, keyword..."
            value={searchText}
            readOnly
          />
          {animationPhase === 'typing' && !typingComplete && (
            <span className="explore-mock-cursor" />
          )}
          <button className="explore-mock-filter-btn">
            <Filter size={12} />
          </button>
          <button
            className={`explore-mock-search-btn ${searchText ? 'active' : ''}`}
          >
            <Search size={12} />
            <span>Search</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="explore-mock-content">
        {/* Trending Topics - visible before results */}
        {animationPhase !== 'results' && (
          <div className="explore-mock-trending">
            <div className="explore-mock-section-header">
              <div className="explore-mock-section-left">
                <TrendingUp size={14} className="explore-mock-section-icon" />
                <h3 className="explore-mock-section-title">Trending Topics</h3>
              </div>
              <span className="explore-mock-section-badge">This Week</span>
            </div>
            <div className="explore-mock-topics-grid">
              {trendingTopics.map((topic, i) => {
                const Icon = topic.icon;
                return (
                  <div
                    key={i}
                    className={`explore-mock-topic explore-mock-topic--${topic.color} ${
                      i < visibleTrendingCards ? 'visible' : ''
                    }`}
                  >
                    <div className="explore-mock-topic-top">
                      <div className={`explore-mock-topic-icon explore-mock-topic-icon--${topic.color}`}>
                        <Icon size={12} />
                      </div>
                      <div className="explore-mock-topic-trend">
                        <ArrowUpRight size={10} />
                        <span>{topic.trend}</span>
                      </div>
                    </div>
                    <span className="explore-mock-topic-title">{topic.title}</span>
                    <div className="explore-mock-topic-meta">
                      <FileText size={10} />
                      <span>{topic.papers} papers</span>
                      <ChevronRight size={10} className="explore-mock-topic-arrow" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Search Results */}
        {animationPhase === 'results' && (
          <div className="explore-mock-results">
            <div className="explore-mock-results-header">
              <div className="explore-mock-results-info">
                <Search size={12} />
                <span>
                  Found <strong>7,082</strong> results for &ldquo;
                  <strong>{searchText}</strong>&rdquo;
                </span>
              </div>
              <div className="explore-mock-results-sort">
                <span>Sort:</span>
                <select className="explore-mock-sort-select">
                  <option>Relevance</option>
                </select>
              </div>
            </div>

            <div className="explore-mock-papers">
              {paperResults.map((paper, i) => (
                <article
                  key={i}
                  className={`explore-mock-paper ${
                    i < visiblePaperCards ? 'visible' : ''
                  }`}
                >
                  <div className="explore-mock-paper-body">
                    <div className="explore-mock-paper-top">
                      <h4 className="explore-mock-paper-title">{paper.title}</h4>
                      <div className="explore-mock-paper-badges">
                        {paper.isOpen && (
                          <span className="explore-mock-pbadge explore-mock-pbadge--open">
                            <Unlock size={9} />
                            Open
                          </span>
                        )}
                        {paper.isTrending && (
                          <span className="explore-mock-pbadge explore-mock-pbadge--trending">
                            <TrendingUp size={9} />
                            Trending
                          </span>
                        )}
                        {paper.isRecommended && (
                          <span className="explore-mock-pbadge explore-mock-pbadge--recommended">
                            <Star size={9} />
                            For You
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="explore-mock-paper-meta">
                      <span className="explore-mock-paper-authors">
                        <Users size={11} />
                        {paper.authors.join(', ')}
                      </span>
                      <span className="explore-mock-paper-sep">·</span>
                      <span className="explore-mock-paper-year">
                        <Calendar size={11} />
                        {paper.year}
                      </span>
                      <span className="explore-mock-paper-sep">·</span>
                      <span className="explore-mock-paper-journal">{paper.journal}</span>
                    </div>
                    <div className="explore-mock-paper-bottom">
                      <span className="explore-mock-paper-field">{paper.field}</span>
                      <span className="explore-mock-paper-citations">
                        <BarChart3 size={11} />
                        {paper.citations.toLocaleString()} citations
                      </span>
                    </div>
                  </div>
                  <div className="explore-mock-paper-actions">
                    <button className="explore-mock-action explore-mock-action--bookmark">
                      <Bookmark size={13} />
                    </button>
                    <button className="explore-mock-action explore-mock-action--link">
                      <ExternalLink size={13} />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExploreMockupContent;
